import React, { useRef } from 'react';

// Utils
import cn from '@/utils/cn';

// Components
import Picto, { type PictoName } from '@/components/atoms/Picto/Picto';

// Styles
import { ColorThemeEnum } from '@/enum/ColorThemeEnum';
import styles from './Button.module.css';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  color?:
    | ColorThemeEnum.ORANGE
    | ColorThemeEnum.GREEN
    | ColorThemeEnum.PURPLE
    | ColorThemeEnum.YELLOW
    | ColorThemeEnum.WHITE;
  variant?: 'bordered' | 'filled';
  size?: 'regular' | 'small';
  className?: string;
  icon?: PictoName | null;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
}

function Button({
  onClick,
  color = ColorThemeEnum.ORANGE,
  size = 'regular',
  icon,
  disabled = false,
  children,
  variant = 'bordered',
  className,
  type = 'button',
}: ButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const buttonBackgroundRef = useRef<HTMLDivElement>(null);

  const handleUpdateBackgroundPosition = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!buttonRef.current || !buttonBackgroundRef.current) return;

    const buttonElement = buttonRef.current;
    const backgroundElement = buttonBackgroundRef.current;
    const buttonWidth = buttonElement.getBoundingClientRect().width;
    const x = event.clientX - buttonElement.getBoundingClientRect().left - buttonWidth / 2;
    const y = event.clientY - buttonElement.getBoundingClientRect().top - buttonWidth / 2;

    backgroundElement.style.left = `${x}px`;
    backgroundElement.style.top = `${y}px`;
  };

  return (
    <button
      ref={buttonRef}
      // eslint-disable-next-line react/button-has-type
      type={type}
      className={cn([styles.button, 'font-uppercase', styles[color], styles[variant], styles[size], className || ''])}
      onMouseMove={handleUpdateBackgroundPosition}
      onClick={onClick}
      disabled={disabled}
    >
      <div className={styles.inner}>
        {children}
        {icon && <Picto icon={icon} width={22} />}
      </div>
      <div ref={buttonBackgroundRef} className={styles.background} />
    </button>
  );
}

export default Button;
